
import React from 'react';
import { StudentOMRModel } from '../../../../models';
import {
    ChevronLeft, Trophy, Calendar, Book,
    CheckCircle2, XCircle, Info, Calculator, HelpCircle
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

interface OMRDetailViewProps {
    result: StudentOMRModel;
    onBack: () => void;
}

const OMRDetailView: React.FC<OMRDetailViewProps> = ({ result, onBack }) => {
    const formatDate = (ts: number) => {
        return new Date(ts).toLocaleDateString('ko-KR', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const getSubjectColor = (subject: string) => {
        const colors: Record<string, string> = {
            '국어': 'bg-rose-500', '수학': 'bg-blue-600', '영어': 'bg-indigo-600',
            '한국사': 'bg-amber-700', '사회': 'bg-orange-600', '과학': 'bg-emerald-600',
        };
        return colors[subject] || 'bg-slate-600';
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header Section */}
            <header className="px-6 py-6 sm:px-10 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="w-10 h-10 flex items-center justify-center bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-100 transition-all">
                        <ChevronLeft size={20} />
                    </button>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg ${getSubjectColor(result.subject)}`}>
                        <Book size={24} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className={`px-2 py-0.5 rounded text-[8px] font-black text-white uppercase tracking-widest ${getSubjectColor(result.subject)}`}>
                                {result.subject}
                            </span>
                            {result.page !== undefined && (
                                <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-indigo-50 text-indigo-600 border border-indigo-100">
                                    PDF {result.page}페이지
                                </span>
                            )}
                            <span className="text-[10px] font-bold text-slate-400">{formatDate(result.completedAt)}</span>
                        </div>
                        <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate leading-tight">
                            {result.bookTitle}
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="bg-indigo-50 px-4 py-2 rounded-2xl border border-indigo-100 flex items-center gap-2">
                        <Trophy size={16} className="text-indigo-600" />
                        <span className="text-sm font-black text-indigo-700">{result.score}점</span>
                    </div>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto p-6 sm:p-10 custom-scrollbar">
                <div className="max-w-4xl mx-auto space-y-10">
                    {/* Summary Card */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <div className="bg-slate-50 rounded-[2rem] p-6 text-center border border-slate-100">
                            <span className="block text-[10px] uppercase font-black text-slate-400 mb-2 tracking-widest">맞은 문항</span>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-3xl font-black text-emerald-600">{result.correctCount}</span>
                                <span className="text-sm font-bold text-slate-400">/ {result.totalCount}</span>
                            </div>
                        </div>
                        <div className="bg-slate-50 rounded-[2rem] p-6 text-center border border-slate-100">
                            <span className="block text-[10px] uppercase font-black text-slate-400 mb-2 tracking-widest">틀린 문항</span>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-3xl font-black text-rose-500">{result.totalCount - result.correctCount}</span>
                                <span className="text-sm font-bold text-slate-400">/ {result.totalCount}</span>
                            </div>
                        </div>
                        <div className="bg-slate-50 rounded-[2rem] p-6 text-center border border-slate-100 flex flex-col justify-center items-center">
                            <span className="block text-[10px] uppercase font-black text-slate-400 mb-2 tracking-widest">세트 제목</span>
                            <span className="text-sm font-black text-slate-700 block truncate mb-1">{result.setTitle}</span>
                            {result.page !== undefined && (
                                <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
                                    PDF {result.page}페이지 출제
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Question Breakdown */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                                <Calculator size={16} />
                            </div>
                            <h4 className="text-lg font-black text-slate-800 tracking-tight">문항별 상세 결과</h4>
                        </div>

                        <div className="grid grid-cols-1 gap-2">
                            {result.questions && result.questions.length > 0 ? (
                                result.questions.map((q: any) => {
                                    const isCorrect = result.userAnswers[q.number] === q.answer;
                                    return (
                                        <div
                                            key={q.number}
                                            className={`flex flex-col p-5 rounded-3xl border transition-all space-y-4 ${
                                                isCorrect ? 'bg-white border-slate-100 shadow-sm' : 'bg-rose-50/10 border-rose-100'
                                            }`}
                                        >
                                            {/* Top Line: Question Number and Badges */}
                                            <div className="flex items-center justify-between border-b border-slate-50 pb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black shadow-sm ${
                                                        isCorrect ? 'bg-slate-50 text-slate-400' : 'bg-rose-500 text-white'
                                                    }`}>
                                                        {q.number}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-black text-slate-800">
                                                            나의 답: {result.userAnswers[q.number] || '-'}
                                                        </span>
                                                        {isCorrect ? (
                                                            <CheckCircle2 size={15} className="text-emerald-500" />
                                                        ) : (
                                                            <XCircle size={15} className="text-rose-500" />
                                                        )}
                                                        {!isCorrect && (
                                                            <span className="text-[10px] font-bold text-rose-400 bg-rose-50 px-1.5 py-0.5 rounded">
                                                                정답: {q.answer}번
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                                    isCorrect ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
                                                }`}>
                                                    {isCorrect ? '정답' : '오답'}
                                                </span>
                                            </div>

                                            {/* Middle Content: Question, Options, and Explanation if available */}
                                            <div className="space-y-3.5 pl-1.5">
                                                {q.questionHtml ? (
                                                    <div className="text-xs font-bold leading-relaxed text-slate-700 select-text prose prose-slate max-w-none">
                                                        <ReactMarkdown
                                                            remarkPlugins={[remarkMath]}
                                                            rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                        >
                                                            {q.questionHtml}
                                                        </ReactMarkdown>
                                                        {q.imageUrl && (
                                                            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/50 flex justify-center items-center shadow-inner p-1" style={{ height: '130px' }}>
                                                                <img 
                                                                    src={q.imageUrl} 
                                                                    alt={`Question ${q.number} diagram`}
                                                                    className="object-contain rounded-xl"
                                                                    style={{ maxHeight: '120px' }}
                                                                />
                                                            </div>
                                                        )}
                                                        {q.imageSvg && (
                                                            <div 
                                                                className="mt-3 overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/50 flex justify-center items-center p-2 shadow-inner svg-diagram [&>svg]:max-h-[120px] [&>svg]:w-auto"
                                                                style={{ height: '130px' }}
                                                                dangerouslySetInnerHTML={{ __html: q.imageSvg }}
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-[11px] font-medium text-slate-400">
                                                        *교재 이미지/텍스트 OMR 마킹 채점 기록입니다.
                                                    </p>
                                                )}

                                                {q.options && q.options.length > 0 && (
                                                    <div className="grid grid-cols-1 gap-2 pl-1.5">
                                                        {q.options.map((opt: string, idx: number) => {
                                                            const num = idx + 1;
                                                            const isMyAns = result.userAnswers[q.number] === String(num);
                                                            const isCorrectAns = String(q.answer) === String(num);
                                                            
                                                            let optColor = "text-slate-500 font-medium";
                                                            let badgeBg = "bg-slate-50 text-slate-400";
                                                            if (isCorrectAns) {
                                                                optColor = "text-emerald-600 font-black";
                                                                badgeBg = "bg-emerald-500 text-white shadow-sm";
                                                            } else if (isMyAns) {
                                                                optColor = "text-rose-500 font-black";
                                                                badgeBg = "bg-rose-500 text-white shadow-sm";
                                                            }

                                                            return (
                                                                <div key={idx} className={`text-[11px] flex items-center gap-2.5 ${optColor}`}>
                                                                    <span className={`w-4.5 h-4.5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0 ${badgeBg}`}>
                                                                        {num}
                                                                    </span>
                                                                    <div className="prose prose-slate max-w-none text-inherit leading-relaxed">
                                                                        <ReactMarkdown
                                                                            remarkPlugins={[remarkMath]}
                                                                            rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                                        >
                                                                            {opt}
                                                                        </ReactMarkdown>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {q.explanationHtml && (
                                                    <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 flex gap-3 text-[11px] leading-relaxed text-slate-500">
                                                        <HelpCircle size={14} className="text-indigo-500 shrink-0 mt-0.5" />
                                                        <div className="prose prose-slate max-w-none">
                                                            <span className="font-black text-slate-700 block mb-1">정답 해설 ({q.answer}번)</span>
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkMath]}
                                                                rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                            >
                                                                {q.explanationHtml}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="p-10 text-center bg-slate-50 rounded-[2rem] border border-dashed border-slate-200">
                                    <Info className="mx-auto text-slate-200 mb-3" />
                                    <p className="text-sm font-bold text-slate-400">상세 문항 정보가 저장되지 않았습니다.</p>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default OMRDetailView;
